import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Bot,
  Check,
  Copy,
  ExternalLink,
  Menu,
  MessageCircleQuestion,
  Plug,
} from 'lucide-react';
import { useChatLayout } from '@/components/layout/ChatLayout';
import { useAuth } from '@/contexts/AuthContext';
import { mcpConnectionsService, type AiApp } from '@/services/mcpConnectionsService';
import Button from '@/components/ui/Button';
import {
  AI_APP_META,
  AI_APP_ORDER,
  CHATGPT_CREATE_CONNECTOR_URL,
  CHATGPT_DEV_MODE_URL,
  CHATGPT_KICKOFF_PROMPT,
  CLAUDE_CODE_ADD_COMMAND,
  CLAUDE_CONNECT_URL,
  MCP_SERVER_URL,
} from '@/config/aiApps';

/**
 * Guided MCP setup — pick your AI app, follow its steps, approve on the Zamino
 * consent screen that opens during the app's sign-in, ask your first question.
 * The status section is live: the page polls the user's MCP connections while
 * open, so it flips to "connected" the moment OAuth completes.
 */

const POLL_MS = 5000;

const openExternal = (url: string): void => {
  window.open(url, '_blank', 'noopener');
};

const HOW_IT_WORKS: Array<{ icon: React.ComponentType<{ className?: string }>; title: string; sub: string }> = [
  { icon: Bot, title: '1. Pick your AI app', sub: 'ChatGPT, Claude, or any MCP-capable client.' },
  { icon: Plug, title: '2. Link it to Zamino', sub: 'Add the connector and sign in with your Zamino account.' },
  { icon: MessageCircleQuestion, title: '3. Approve & ask', sub: 'Approve on the consent screen, then ask about your business.' },
];

export default function ConnectAiPage() {
  const { isMobile, toggleSidebar } = useChatLayout();
  const { user } = useAuth();
  const [selected, setSelected] = useState<AiApp | null>(null);

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-3xl mx-auto">
        <Link
          to="/home"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <h1 className="text-3xl font-bold text-foreground mt-5">Connect your AI</h1>
        <p className="text-muted-foreground mt-1 max-w-xl">
          Ask about your businesses in the AI you already use. Connect once — takes about 2 minutes.
        </p>

        {/* How it works */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-xl bg-muted border border-border p-5 mt-6">
          {HOW_IT_WORKS.map(({ icon: Icon, title, sub }) => (
            <div key={title}>
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Icon className="w-4 h-4 text-primary" /> {title}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{sub}</p>
            </div>
          ))}
        </div>

        {/* Picker */}
        <h2 className="text-[15px] font-semibold text-foreground mt-8 mb-3">Choose your AI app</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {AI_APP_ORDER.map((app) => (
            <button
              key={app}
              onClick={() => setSelected(app)}
              className={clsx(
                'flex flex-col items-center gap-2.5 rounded-lg border p-4 transition-colors',
                selected === app
                  ? 'border-primary ring-1 ring-primary bg-accent'
                  : 'border-border bg-card hover:border-input hover:bg-accent',
              )}
            >
              {AI_APP_META[app].logo ? (
                <img src={AI_APP_META[app].logo!} alt="" className="w-9 h-9 rounded-lg" />
              ) : (
                <span className="w-9 h-9 rounded-lg bg-muted grid place-items-center">
                  <Bot className="w-5 h-5 text-muted-foreground" />
                </span>
              )}
              <span className="text-sm font-medium text-foreground">
                {app === 'other' ? 'Other MCP client' : AI_APP_META[app].label}
              </span>
            </button>
          ))}
        </div>

        {selected && <Guide key={selected} app={selected} email={user?.email ?? 'your Zamino email'} />}

        <p className="text-xs text-muted-foreground mt-8">
          Manage or revoke access anytime in{' '}
          <Link to="/settings/external-ai-access" className="underline underline-offset-2 hover:text-foreground">
            Settings → External AI Access
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

/* --------------------------------- Guide --------------------------------- */

const Guide: React.FC<{ app: AiApp; email: string }> = ({ app, email }) => {
  const steps = guideSteps(app, email);
  const afterConnect = postConnectStep(app);
  return (
    <div className="rounded-xl border border-border bg-card shadow-card p-5 sm:p-6 mt-5">
      <div className="flex items-center gap-2.5 mb-5">
        {AI_APP_META[app].logo ? (
          <img src={AI_APP_META[app].logo!} alt="" className="w-6 h-6 rounded" />
        ) : (
          <Bot className="w-6 h-6 text-muted-foreground" />
        )}
        <h3 className="text-[15px] font-semibold text-foreground">
          Connect {app === 'other' ? 'your MCP client' : AI_APP_META[app].label}
        </h3>
      </div>

      <ol className="space-y-5">
        {steps.map((step, i) => (
          <Step key={i} n={i + 1} title={step.title}>
            {step.body}
          </Step>
        ))}
        <Step n={steps.length + 1} title="Approve on the consent screen" last={!afterConnect}>
          <ConnectionStatus app={app} />
        </Step>
        {afterConnect && (
          <Step n={steps.length + 2} title={afterConnect.title} last>
            {afterConnect.body}
          </Step>
        )}
      </ol>
    </div>
  );
};

interface GuideStep {
  title: string;
  body: React.ReactNode;
}

function guideSteps(app: AiApp, email: string): GuideStep[] {
  switch (app) {
    case 'claude':
      return [
        {
          title: 'Add Zamino to Claude',
          body: (
            <>
              <Button size="sm" onClick={() => openExternal(CLAUDE_CONNECT_URL)}>
                <ExternalLink className="w-4 h-4 mr-1.5" /> Add to Claude
              </Button>
              <p className="text-xs text-muted-foreground mt-2.5">
                Or add it manually in Claude: Settings → Connectors → Add custom connector, with this URL:
              </p>
              <CopyBox value={MCP_SERVER_URL} />
            </>
          ),
        },
        {
          title: 'Connect and sign in',
          body: (
            <p className="text-sm text-muted-foreground">
              In Claude, click <Kbd>Add</Kbd> then <Kbd>Connect</Kbd>, and sign in with{' '}
              <span className="font-medium text-foreground">{email}</span>.
            </p>
          ),
        },
      ];
    case 'chatgpt':
      return [
        {
          title: 'Turn on Developer mode',
          body: (
            <>
              <Button size="sm" onClick={() => openExternal(CHATGPT_DEV_MODE_URL)}>
                <ExternalLink className="w-4 h-4 mr-1.5" /> Turn on Developer mode
              </Button>
              <p className="text-xs text-muted-foreground mt-2.5">
                Opens ChatGPT → Settings → Security. Switch on <Kbd>Developer mode</Kbd> — it needs a paid ChatGPT
                plan, and on Business/Enterprise workspaces an admin may have to allow it first.
              </p>
            </>
          ),
        },
        {
          title: 'Create your connector',
          body: (
            <>
              <Button size="sm" onClick={() => openExternal(CHATGPT_CREATE_CONNECTOR_URL)}>
                <ExternalLink className="w-4 h-4 mr-1.5" /> Open the new-connector form
              </Button>
              <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1 mt-3">
                <li>
                  Name it <span className="font-medium text-foreground">Zamino</span>
                </li>
                <li>Paste the URL below:</li>
              </ul>
              <CopyBox value={MCP_SERVER_URL} />
              <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1 mt-2">
                <li>
                  Leave Authentication as <span className="font-medium text-foreground">OAuth</span>
                </li>
                <li>
                  Tick <Kbd>I understand and want to continue</Kbd>, then click <Kbd>Create</Kbd>
                </li>
              </ul>
            </>
          ),
        },
        {
          title: 'Hit "Connect", then sign in',
          body: (
            <p className="text-sm text-muted-foreground">
              ChatGPT opens a Zamino sign-in — use{' '}
              <span className="font-medium text-foreground">{email}</span>.
            </p>
          ),
        },
      ];
    case 'claude_code':
      return [
        {
          title: 'Add the server from your terminal',
          body: <CopyBox value={CLAUDE_CODE_ADD_COMMAND} />,
        },
        {
          title: 'Authenticate',
          body: (
            <p className="text-sm text-muted-foreground">
              In Claude Code, run <Kbd>/mcp</Kbd>, pick <span className="font-medium text-foreground">zamino</span>,
              and sign in with <span className="font-medium text-foreground">{email}</span>.
            </p>
          ),
        },
      ];
    case 'other':
      return [
        {
          title: 'Point your client at Zamino',
          body: (
            <>
              <CopyBox value={MCP_SERVER_URL} />
              <p className="text-xs text-muted-foreground mt-2">
                Works with any client that supports Streamable HTTP MCP servers with OAuth sign-in. Sign in with{' '}
                <span className="font-medium text-foreground">{email}</span>.
              </p>
            </>
          ),
        },
      ];
  }
}

/** Optional step rendered *after* the live connection status. */
function postConnectStep(app: AiApp): GuideStep | null {
  if (app !== 'chatgpt') return null;
  return {
    title: 'Finish setup in ChatGPT',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Start a new chat, enable <span className="font-medium text-foreground">Zamino</span> from the composer&apos;s
          tools menu, then paste this in:
        </p>
        <CopyBox value={CHATGPT_KICKOFF_PROMPT} wrap copyLabel="Copy prompt" />
      </>
    ),
  };
}

/* ---------------------------- Live connection ----------------------------- */

/**
 * The interactive part: while the guide is open we poll the user's MCP
 * connections. Consent happens in the browser during the app's OAuth sign-in
 * (the Zamino consent screen); the moment the exchange completes, the
 * connection appears here and this flips to the connected state.
 */
const ConnectionStatus: React.FC<{ app: AiApp }> = ({ app }) => {
  const connectionsQuery = useQuery({
    queryKey: ['mcpConnections'],
    queryFn: mcpConnectionsService.list,
    refetchInterval: POLL_MS,
  });

  const connections = (connectionsQuery.data?.connections ?? []).filter((c) => c.app === app);
  const approved = connections.find((c) => c.status === 'approved');
  const label = app === 'other' ? 'your MCP client' : AI_APP_META[app].label;

  if (approved) {
    const openUrl = AI_APP_META[app].openUrl;
    return (
      <div className="rounded-lg border border-success/30 bg-success/10 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-success">
          <Check className="w-4 h-4" /> {label} is connected
        </div>
        <p className="text-sm text-muted-foreground mt-1.5">
          Try it: ask <span className="italic">“How were sales last week?”</span>
        </p>
        <div className="flex gap-2 flex-wrap mt-3">
          {openUrl && (
            <Button size="sm" onClick={() => openExternal(openUrl)}>
              <ExternalLink className="w-4 h-4 mr-1.5" /> Open {AI_APP_META[app].label}
            </Button>
          )}
          <Link to="/home">
            <Button size="sm" variant="outline">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-dashed border-border bg-muted/50 p-4">
      <span className="relative flex w-2.5 h-2.5 flex-none">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
        <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-primary" />
      </span>
      <p className="text-sm text-muted-foreground">
        Waiting for {label} to connect — during its sign-in, Zamino opens a consent screen; approve
        there and this flips automatically.
      </p>
    </div>
  );
};

/* ------------------------------- Primitives ------------------------------- */

const Step: React.FC<{ n: number; title: string; last?: boolean; children: React.ReactNode }> = ({
  n,
  title,
  last,
  children,
}) => (
  <li className="relative flex gap-3.5">
    {!last && <span className="absolute left-[13px] top-8 bottom-[-20px] w-px bg-border" aria-hidden />}
    <span className="w-[27px] h-[27px] rounded-full bg-muted border border-border grid place-items-center text-[12px] font-bold text-muted-foreground flex-none">
      {n}
    </span>
    <div className="min-w-0 flex-1 pt-0.5">
      <div className="text-sm font-semibold text-foreground mb-1.5">{title}</div>
      {children}
    </div>
  </li>
);

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground">
    {children}
  </span>
);

const CopyBox: React.FC<{ value: string; wrap?: boolean; copyLabel?: string }> = ({ value, wrap, copyLabel }) => {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Couldn’t copy — select the text and copy it manually.');
    }
  };
  return (
    <div
      className={clsx(
        'flex gap-2 rounded-lg border border-input bg-muted px-3 py-2 mt-2 max-w-xl',
        wrap ? 'items-start' : 'items-center',
      )}
    >
      <code className={clsx('text-[12.5px] text-foreground flex-1', wrap ? 'whitespace-pre-wrap' : 'truncate')}>
        {value}
      </code>
      <button
        onClick={copy}
        className={clsx(
          'flex-none inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors',
          wrap && 'mt-0.5',
        )}
        aria-label="Copy to clipboard"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? 'Copied' : (copyLabel ?? 'Copy')}
      </button>
    </div>
  );
};
